import React, { useEffect, useMemo, useState } from "react";
import { docFileName } from "@/lib/utils";
import api, { API, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Clock, Play, Send, SearchCheck } from "lucide-react";
import { formatDOP, formatDate } from "@/lib/format";
import PrintMenu from "@/components/PrintMenu";
import DocSearchBar from "@/components/DocSearchBar";

async function downloadPdf(noteId, fallback) {
  const r = await fetch(`${API}/credit-debit-notes/${noteId}/pdf`, { credentials: "include" });
  if (!r.ok) { toast.error(`Error descargando PDF (${r.status})`); return; }
  const cd = r.headers.get("content-disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  const name = m ? m[1] : `${fallback}.pdf`;
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(u);
}

function NoteBuilder({ kind, open, onOpenChange, onSubmit, ncfTypes }) {
  // kind: "credit" | "debit"
  const [invoices, setInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("");
  const [restock, setRestock] = useState(true);
  const [ncfType, setNcfType] = useState("");

  const allowedTypes = useMemo(() => {
    const ok = kind === "credit" ? ["E34"] : ["E33"];
    return ncfTypes.filter((s) => ok.includes(s.type_code) && s.active);
  }, [ncfTypes, kind]);

  useEffect(() => {
    if (open) {
      // Para NC usa endpoint filtrado (mismo mes fiscal + sin NC previa).
      // Para ND usa el listado completo de facturas emitidas.
      const url = kind === "credit" ? "/credit-notes/eligible-invoices" : "/invoices";
      api.get(url).then(({ data }) => {
        const list = kind === "credit" ? data : (data || []).filter((i) => i.status === "emitida");
        setInvoices(list);
      });
      setItems([]); setInvoiceId(""); setReason(""); setRestock(true);
      if (allowedTypes.length > 0) setNcfType(allowedTypes[0].type_code);
    }
    // eslint-disable-next-line
  }, [open, kind, allowedTypes]);

  useEffect(() => {
    if (invoiceId) {
      const inv = invoices.find((i) => i.id === invoiceId);
      if (inv) setItems(inv.items.map((it) => ({ ...it, quantity: 0, _max: it.quantity, _orig_price: it.price,
        price: kind === "credit" ? it.price : 0, discount: 0 })));
    }
  }, [invoiceId, invoices, kind]);

  const totals = useMemo(() => {
    let subtotal = 0, itbis = 0;
    items.forEach((it) => {
      const line = it.quantity * it.price - (it.discount || 0);
      if (line <= 0) return;
      subtotal += line;
      if (it.itbis_applies) itbis += line * 0.18;
    });
    return { subtotal, itbis, total: subtotal + itbis };
  }, [items]);

  const submit = () => {
    const valid = items.filter((it) => it.quantity > 0 && it.price > 0);
    if (valid.length === 0) return toast.error(kind === "credit" ? "Indica al menos un item con cantidad y monto > 0"
      : "Indica la cantidad y el aumento por unidad de al menos un item");
    const over = valid.find((it) => it.quantity > it._max || (kind === "credit" && it.price > it._orig_price));
    if (over) return toast.error(kind === "credit" ? `${over.name}: no puedes acreditar más cantidad o precio que lo facturado`
      : `${over.name}: el cargo no puede aplicarse a más unidades que las facturadas`);
    if (!invoiceId) return toast.error("Selecciona una factura");
    if (!ncfType) return toast.error("Selecciona tipo NCF");
    onSubmit({
      invoice_id: invoiceId, ncf_type: ncfType, reason, restock,
      items: valid.map((it) => ({
        product_id: it.product_id, sku: it.sku || "", name: it.name,
        quantity: Number(it.quantity), price: Number(it.price),
        discount: Number(it.discount || 0), itbis_applies: it.itbis_applies,
      })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva nota de {kind === "credit" ? "crédito" : "débito"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="overline">Factura referenciada</Label>
            <Select value={invoiceId} onValueChange={setInvoiceId}>
              <SelectTrigger className="mt-1" data-testid="note-invoice-select"><SelectValue placeholder="Selecciona factura" /></SelectTrigger>
              <SelectContent>
                {invoices.slice(0, 100).map((inv) => (
                  <SelectItem key={inv.id} value={inv.id}>{inv.number} · {inv.customer_name} · {formatDOP(inv.total)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Tipo NCF</Label>
            <Select value={ncfType} onValueChange={setNcfType}>
              <SelectTrigger className="mt-1" data-testid="note-ncf-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {allowedTypes.map((s) => (
                  <SelectItem key={s.id} value={s.type_code}>{s.type_code} — {s.description}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="overline">Motivo</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="Devolución, ajuste de precio, descuento, etc." />
          </div>
          {kind === "credit" && (
            <div className="col-span-2 flex items-center gap-3 mt-1">
              <Switch checked={restock} onCheckedChange={setRestock} id="restock" />
              <Label htmlFor="restock" className="text-sm">Devolver al stock</Label>
            </div>
          )}
        </div>

        <div className="mt-4">
          <Label className="overline">Items {kind === "credit" ? "a acreditar (devolver / descontar)" : "a debitar (cargo adicional)"}</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="note-items-hint">
            {kind === "credit"
              ? "Devolución: cantidad devuelta al precio facturado. Rebaja de precio: cantidad afectada y el descuento por unidad."
              : "Escribe solo el AUMENTO por unidad (diferencia), no el precio nuevo. Ej.: precio 500 → 550 en 10 unidades = 10 × 50."}
          </p>
          <div className="grid grid-cols-[1fr,80px,110px,110px] gap-2 px-2 pt-2 overline text-[10px]">
            <div>Producto</div><div>Cant.</div><div>{kind === "credit" ? "Monto x unidad" : "Aumento x unidad"}</div><div className="text-right">Subtotal</div>
          </div>
          <div className="border border-border rounded-md mt-1 max-h-[300px] overflow-y-auto">
            {items.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Selecciona una factura para cargar los items</div>}
            {items.map((it, idx) => (
              <div key={idx} className="p-2 border-b border-border/60 text-sm grid grid-cols-[1fr,80px,110px,110px] gap-2 items-center">
                <div className="truncate">
                  <div className="font-semibold">{it.name}</div>
                  <div className="text-xs text-muted-foreground data-cell">{it.sku} · Facturado: {it._max} × {formatDOP(it._orig_price)}</div>
                </div>
                <Input type="number" min="0" max={kind === "credit" ? it._max : undefined} step="1" inputMode="decimal" value={it.quantity} onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, quantity: Number(e.target.value) } : x))} className="h-8 font-mono" data-testid={`note-item-qty-${idx}`} />
                <Input type="number" min="0" step="0.01" max={kind === "credit" ? it._orig_price : undefined} value={it.price}
                  onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, price: Number(e.target.value) } : x))}
                  className="h-8 font-mono" data-testid={`note-item-price-${idx}`} />
                <div className="text-right data-cell text-xs">{formatDOP((it.quantity * it.price) - (it.discount || 0))}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-right text-sm space-y-0.5">
            <div>Subtotal: <span className="data-cell">{formatDOP(totals.subtotal)}</span></div>
            <div>ITBIS: <span className="data-cell">{formatDOP(totals.itbis)}</span></div>
            <div className="font-bold">Total {kind === "credit" ? "a acreditar" : "a debitar"}: <span className="data-cell text-primary">{formatDOP(totals.total)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-primary font-bold" data-testid="save-note-btn">Emitir {kind === "credit" ? "NC" : "ND"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotesTable({ items, kind, onReload }) {
  const isCredit = kind === "credit";
  return (
    <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid={`${kind}-notes-table`}>
      <table className="w-full text-sm">
        <thead className="overline border-b border-border">
          <tr>
            <th className="text-left p-3">Número</th>
            <th className="text-left p-3">NCF</th>
            <th className="text-left p-3">Factura ref.</th>
            <th className="text-left p-3">Cliente</th>
            {isCredit && <th className="text-left p-3">Código NC</th>}
            {isCredit && <th className="text-left p-3">Vence</th>}
            {isCredit && <th className="text-left p-3">Estado</th>}
            <th className="text-left p-3">Motivo</th>
            <th className="text-left p-3">Fecha</th>
            <th className="text-right p-3">Monto</th>
            <th className="text-right p-3 w-24">PDF</th>
          </tr>
        </thead>
        <tbody>
          {items.map((n) => {
            const now = new Date();
            const expired = n.expires_at && new Date(n.expires_at) < now && n.redemption_status === "disponible";
            const effectiveStatus = expired ? "vencida" : (n.redemption_status || "disponible");
            const badgeCls =
              effectiveStatus === "disponible" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              : effectiveStatus === "usada"    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
              : effectiveStatus === "vencida"  ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
              :                                    "bg-muted text-muted-foreground";
            return (
              <tr key={n.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 data-cell font-bold">{n.number}</td>
                <td className="p-3 data-cell text-xs">{n.ncf}</td>
                <td className="p-3 data-cell text-xs">{n.invoice_number}</td>
                <td className="p-3">{n.customer_name}</td>
                {isCredit && (
                  <td className="p-3 data-cell font-mono text-xs" data-testid={`nc-code-${n.number}`}>
                    {n.redemption_code || "—"}
                  </td>
                )}
                {isCredit && (
                  <td className={`p-3 text-xs ${expired ? "text-red-600 dark:text-red-400 font-semibold" : "text-muted-foreground"}`}>
                    {n.expires_at ? formatDate(n.expires_at) : "—"}
                  </td>
                )}
                {isCredit && (
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${badgeCls}`} data-testid={`nc-status-${n.number}`}>
                      {effectiveStatus}
                    </span>
                  </td>
                )}
                <td className="p-3 text-xs">{n.reason || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground">{formatDate(n.created_at)}</td>
                <td className="p-3 text-right data-cell font-bold">{formatDOP(n.total)}</td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end items-center">
                    {n.is_electronic && (
                      <Button
                        size="icon" variant="ghost"
                        data-testid={`note-send-ecf-${n.number}`}
                        title={n.ecf_status === "aceptado" ? "Reenviar a DGII" : "Enviar a DGII"}
                        onClick={async () => {
                          try {
                            await api.post(`/credit-debit-notes/${n.id}/send-ecf`);
                            toast.success("e-CF enviado a DGII");
                            onReload && onReload();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Error enviando e-CF");
                          }
                        }}
                      >
                        <Send className="w-4 h-4 text-primary" />
                      </Button>
                    )}
                    {n.is_electronic && n.ecf_track_id && (
                      <Button
                        size="icon" variant="ghost"
                        data-testid={`note-verify-ecf-${n.number}`}
                        title="Verificar estado en DGII (trackId)"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/dgii/phase4/refresh-status/${n.ncf}`);
                            const est = data.new_status || "desconocido";
                            if (est === "aceptado") toast.success(`DGII: Aceptado · ${n.ncf}`);
                            else if (est === "rechazado") toast.error(`DGII: Rechazado · ${data.error || n.ncf}`);
                            else toast.message(`DGII: ${est} · ${n.ncf}`);
                            onReload && onReload();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Error verificando DGII");
                          }
                        }}
                      >
                        <SearchCheck className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    )}
                    <PrintMenu
                      url={`/credit-debit-notes/${n.id}/pdf`}
                      filename={docFileName(n)}
                      label=""
                      iconOnly
                      variant="ghost"
                      size="icon"
                      testid={`note-pdf-${n.number}`}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr><td colSpan={isCredit ? 11 : 8} className="p-8 text-center text-muted-foreground">Sin notas</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function NotesTab({ items, kind, onNew, onReload, ncfTypes, open, setOpen, submit }) {
  const [search, setSearch] = useState("");
  const [criteria, setCriteria] = useState({ number: true, ncf: true, invoice: true, customer: true, reason: true });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((n) => {
      if (criteria.number && (n.number || "").toLowerCase().includes(s)) return true;
      if (criteria.ncf && (n.ncf || "").toLowerCase().includes(s)) return true;
      if (criteria.invoice && (n.invoice_number || "").toLowerCase().includes(s)) return true;
      if (criteria.customer && (n.customer_name || "").toLowerCase().includes(s)) return true;
      if (criteria.reason && (n.reason || "").toLowerCase().includes(s)) return true;
      return false;
    });
  }, [items, search, criteria]);

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button onClick={onNew} className="bg-primary font-bold" data-testid={`new-${kind}-btn`}>
          <Plus className="w-4 h-4 mr-1.5" /> {kind === "credit" ? "Nueva NC" : "Nueva ND"}
        </Button>
      </div>
      <DocSearchBar
        value={search}
        onChange={setSearch}
        criteria={criteria}
        onCriteriaChange={setCriteria}
        options={[
          { key: "number", label: "Número" },
          { key: "ncf", label: "NCF" },
          { key: "invoice", label: "Factura" },
          { key: "customer", label: "Cliente" },
          { key: "reason", label: "Motivo" },
        ]}
        placeholder={`Buscar nota de ${kind === "credit" ? "crédito" : "débito"}…`}
        testidPrefix={`${kind}-notes-search`}
        totalLabel={`${filtered.length} de ${items.length}`}
      />
      <NotesTable items={filtered} kind={kind} onReload={onReload} />
      <NoteBuilder kind={kind} open={open} onOpenChange={setOpen} onSubmit={submit} ncfTypes={ncfTypes} />
    </>
  );
}

function ExpireCheckPanel() {
  const [state, setState] = useState({ next_run: null, runs: [] });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/credit-notes/scheduler-runs"); setState(data); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/credit-notes/expire-check");
      toast.success(
        data.expired > 0
          ? `${data.expired} NC${data.expired === 1 ? "" : "s"} vencida${data.expired === 1 ? "" : "s"} — ingreso registrado: ${formatDOP(data.total_amount)}`
          : "Sin NCs para vencer"
      );
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const lastRun = state.runs?.[0];
  return (
    <div className="border border-border rounded-md bg-card p-3 mb-4 flex flex-wrap items-center gap-4" data-testid="expire-check-panel">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-primary" />
        <div>
          <div className="text-xs overline text-muted-foreground">Vencimiento automático de NCs</div>
          <div className="text-sm">
            {state.next_run ? (
              <>Próxima corrida: <span className="font-semibold" data-testid="expire-next-run">{formatDate(state.next_run)}</span></>
            ) : (
              <span className="text-muted-foreground">Scheduler no activo</span>
            )}
          </div>
        </div>
      </div>
      {lastRun && (
        <div className="text-xs text-muted-foreground border-l border-border pl-4">
          <div>Última corrida: {formatDate(lastRun.run_at)}</div>
          <div data-testid="expire-last-run">
            Actor: <span className="font-mono">{lastRun.actor}</span> · Vencidas: <span className="font-bold text-foreground">{lastRun.expired}</span> · Ingreso: <span className="font-bold text-foreground">{formatDOP(lastRun.total_amount || 0)}</span>
          </div>
        </div>
      )}
      <div className="ml-auto">
        <Button size="sm" variant="outline" onClick={runNow} disabled={busy} data-testid="expire-run-now-btn">
          <Play className="w-3 h-3 mr-1" /> {busy ? "Ejecutando…" : "Ejecutar ahora"}
        </Button>
      </div>
    </div>
  );
}


export default function CreditDebitNotes() {
  const [credit, setCredit] = useState([]);
  const [debit, setDebit] = useState([]);
  const [ncfTypes, setNcfTypes] = useState([]);
  const [openCredit, setOpenCredit] = useState(false);
  const [openDebit, setOpenDebit] = useState(false);

  const load = async () => {
    const [c, d, n] = await Promise.all([
      api.get("/credit-notes"), api.get("/debit-notes"), api.get("/settings/ncf-sequences?usable=true"),
    ]);
    setCredit(c.data); setDebit(d.data); setNcfTypes(n.data);
  };
  useEffect(() => { load(); }, []);

  const submit = (kind) => async (payload) => {
    try {
      await api.post(kind === "credit" ? "/credit-notes" : "/debit-notes", payload);
      toast.success(`Nota de ${kind === "credit" ? "crédito" : "débito"} emitida`);
      if (kind === "credit") setOpenCredit(false); else setOpenDebit(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Notas de Crédito / Débito"
        subtitle="Ajustes posteriores a una factura. Las NC reducen el saldo y devuelven stock; las ND agregan cargos."
        testid="notes-header"
      />
      <Tabs defaultValue="credit">
        <TabsList className="mb-4">
          <TabsTrigger value="credit" data-testid="tab-credit">Notas de Crédito (E34)</TabsTrigger>
          <TabsTrigger value="debit" data-testid="tab-debit">Notas de Débito (E33)</TabsTrigger>
        </TabsList>
        <TabsContent value="credit">
          <ExpireCheckPanel />
          <NotesTab
            items={credit} kind="credit" ncfTypes={ncfTypes}
            open={openCredit} setOpen={setOpenCredit}
            onNew={() => setOpenCredit(true)} submit={submit("credit")}
            onReload={load}
          />
        </TabsContent>
        <TabsContent value="debit">
          <NotesTab
            items={debit} kind="debit" ncfTypes={ncfTypes}
            open={openDebit} setOpen={setOpenDebit}
            onNew={() => setOpenDebit(true)} submit={submit("debit")}
            onReload={load}
          />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
